import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ReMixChart } from "../../../../../../components/recharts/ReMixCharts";
import { ChartType } from "../../../../../../models/chart_model";
import loader from "../../../../../../components/recharts/loader.gif";
import { getTopBuyerSellerDelta, getTopBuyerSellerSlotwiseDelta, TopBuyerSellerDeltaResponse } from "../../../../../../Rest_api/RestApi";

export default function BuySellDelta({
  selectedBlock,
  refreshKey,
  setError,
  selectedExchanges = [],
  selectedMarkets = [],
  view,
  setSharedData,
  comparisonDates,
}: {
  selectedBlock?: any[];
  refreshKey: number;
  setError?: any;
  setUniqueTargetDate?: any;
  selectedExchanges?: string[];
  selectedMarkets?: string[];
  view: "drawal" | "injection";
  setSharedData?: (data: any) => void;
  comparisonDates?: any[];
}) {
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<TopBuyerSellerDeltaResponse | null>(null);
  const [availableDates, setAvailableDates] = useState<any[]>([]);

  // Sync available dates from Redux
  useEffect(() => {
    const dateKey = "Power Market";
    const trendDates = DefaultDates?.[dateKey]?.defaultTrendDates || [];
    setAvailableDates(trendDates);
  }, [DefaultDates]);

  useEffect(() => {
    const fetchDeltaData = async () => {
      const datesToUse = comparisonDates || availableDates;

      if (datesToUse.length < 2) {
        setData(null);
        setSharedData?.(null);
        return;
      }

      try {
        setLoading(true);
        // Take the first two selected date ranges
        // trendDates structure: { value: "YYYY-MM-DD,YYYY-MM-DD", label: "..." }
        const range1 = datesToUse[0].value.split(",");
        const range2 = datesToUse[1].value.split(",");

        const startDate1 = range1[0];
        const endDate1 = range1[1] || startDate1; // Handle single date selection

        const startDate2 = range2[0];
        const endDate2 = range2[1] || startDate2;

        let response;
        if (selectedBlock && selectedBlock.length > 0) {
          response = await getTopBuyerSellerSlotwiseDelta(
            startDate1,
            endDate1,
            startDate2,
            endDate2,
            selectedExchanges,
            selectedMarkets,
            selectedBlock
          );
        } else {
          response = await getTopBuyerSellerDelta(
            startDate1,
            endDate1,
            startDate2,
            endDate2,
            selectedExchanges,
            selectedMarkets
          );
        }
        setData(response);
        setSharedData?.(response);
      } catch (err) {
        console.error("Error fetching buy/sell delta data", err);
        setError?.("Failed to fetch delta data");
        setData(null);
        setSharedData?.(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDeltaData();
  }, [comparisonDates, availableDates, refreshKey, (selectedExchanges || []).join(","), (selectedMarkets || []).join(","), (selectedBlock || []).join(","), setError, setSharedData]);

  const chartData = useMemo(() => {
    if (!data) return [];
    const source = view === "drawal"
      ? data.largest_drawal_changes
      : data.largest_injection_changes;
    return source.map(item => ({
      ...item,
      name: item.name.length > 12 ? item.name.substring(0, 12) + "..." : item.name
    }));
  }, [data, view]);

  const legends = useMemo(() => {
    return [{
      dataKey: "delta",
      name: "Delta",
      type: ChartType.Bar,
      unit: "MWh",
    }];
  }, []);

  // Calculate colors based on delta value
  const barColors = useMemo(() => {
    return chartData.map((item) => (item.delta < 0 ? "#FF7474" : "#55BF4F"));
  }, [chartData]);

  const datesToCheck = comparisonDates || availableDates;

  return (
    <>
      {loading ? (
        <div className="w-full flex-1 bg-white flex items-center justify-center">
          <img src={loader} width={200} alt="loader" className="block" />
        </div>
      ) : datesToCheck.length < 2 ? (
        <div className="w-full flex-1 bg-white flex items-center justify-center">
          <p className="text-gray-500">Please select 2 date ranges to view Delta</p>
        </div>
      ) : !data || chartData.length === 0 ? (
        <div className="w-full flex-1 bg-white flex items-center justify-center">
          <p className="text-gray-500">No data available</p>
        </div>
      ) : (
        <ReMixChart
          chartHeight="calc(100% - 50px)"
          data={chartData}
          legends={legends}
          xDataKey="name"
          isTimeSlot={false}
          yAxisLabel="MWh"
          showBrush={false}
          showToolbox={false}
          top={10}
          bottom={30}
          barCategoryGap={0}
          xAxisInterval={0}
          fontSize={10}
          colorByPoint={true}
          barColors={barColors}
        />
      )}
    </>
  );
}